import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import UserMenu from './UserMenu';
import HelpMenu from './HelpMenu';
import HelpAssistantPanel from './HelpAssistantPanel';
import WhatIsAnAiServiceDialog from './WhatIsAnAiServiceDialog';
import {
  useLayoutModeFromContext as useLayoutMode,
  useAssistantSidebarPreference,
} from '../../contexts/UIPreferencesContext';
import { AssistantProvider, useOptionalAssistant } from '../../providers/AssistantProvider';
import {
  AssistantFloatingLauncher,
  AssistantNavLauncher,
  AssistantSidebar,
  useAssistantPresentation,
  type AssistantPresentation,
} from './AssistantSidebar';
import { useAuthContext } from '../../contexts/AuthContext';
import UsageIndicator from '../ui/UsageIndicator';
import NoPlanBanner from '../ui/NoPlanBanner';
import SpendingBanner from '../ui/SpendingBanner';
import { useBillingReturn } from '../../hooks/useBillingReturn';
import { useFlag } from '../../hooks/useFeatureFlags';
import type { FlagName } from '../../types/feature-flags.generated';

interface NavLink {
  name: string;
  href: string;
  flagKey: FlagName;
}

interface NavGroup {
  name: string;
  testId: string;
  children: NavLink[];
}

interface AppLayoutProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

/**
 * Tiered navigation model (task 377 restructure).
 *
 * Three top-nav destinations: Dashboard (observe) · AI Services (build/refine)
 * · Advanced ▾ (cross-service admin building blocks). Model Catalog, Model Configs and MCP
 * Connections are account *resources*, so they live in the Advanced dropdown
 * (task 626) alongside Configs/Agents/Prompts/Message Routes — no longer in the
 * avatar menu. There is no Settings dropdown in the top nav; the avatar menu is
 * the door into the `/settings` shell (task 625).
 *
 * Removed nav entries (task 377): Home (`/` now lands on the Dashboard when
 * its flag is on), Composer (`/composer` redirects to `/services`), UI
 * Composer (its `/compose` page was retired outright in task 1132), and the
 * old AI Services → `/endpoints` link (the `/endpoints*` pages remain Advanced's technical
 * view, still routable behind their flag).
 *
 * The standalone Runner and the global Collaborations list have been absorbed
 * by the endpoint hub (task 114): the per-endpoint Test tab replaces Runner,
 * and endpoint-scoped Runs tabs replace the global Collaborations list. The
 * `/collaborations` route stays alive as a demoted cross-endpoint debugging
 * view (reachable from Home's failure strip), so it no longer has a nav slot.
 */
const PLAIN_LINKS: NavLink[] = [
  { name: 'Dashboard', href: '/dashboard', flagKey: 'innovate.fe.dashboard.page' },
  { name: 'AI Services', href: '/services', flagKey: 'innovate.fe.services.page' },
];

const NAV_GROUPS: NavGroup[] = [
  {
    name: 'Advanced',
    testId: 'nav-group-advanced',
    children: [
      // Endpoints leads the group as Advanced's technical view of the same
      // things the top-level AI Services link shows (task 787). It sits above
      // Configs/Agents/Prompts because those are what an endpoint is built
      // FROM — listing the parts before the whole would invert the hierarchy.
      // Until now the page was reachable only by typing its URL.
      { name: 'Endpoints', href: '/endpoints', flagKey: 'innovate.fe.endpoints.page' },
      { name: 'Configs', href: '/configs', flagKey: 'innovate.fe.configs.page' },
      { name: 'Agents', href: '/agents', flagKey: 'innovate.fe.agents.page' },
      { name: 'Prompts', href: '/prompts', flagKey: 'innovate.fe.prompts.page' },
      // Knowledge (task 574): Documents is the primary surface — the account
      // library where upload and ingestion status live — with Knowledge Bases
      // beside it as the groupings that decide which agent sees what.
      { name: 'Documents', href: '/knowledge-documents', flagKey: 'innovate.fe.knowledge.documents.page' },
      { name: 'Knowledge Bases', href: '/knowledge-bases', flagKey: 'innovate.fe.knowledge.bases.page' },
      // Artifacts (task 828): what collaborations PRODUCED, as opposed to the
      // parts above, which are what they are built from. It sits after the
      // building blocks for that reason — output reads after inputs.
      { name: 'Artifacts', href: '/artifacts', flagKey: 'innovate.fe.artifacts.page' },
      { name: 'Message Routes', href: '/message-routes', flagKey: 'innovate.fe.message-routes.page' },
      // Account resources moved out of the avatar menu (task 626); their routes
      // are unchanged, each still gated by its own page flag.
      // Models (task 1167): one page for Model Providers, their Models and the
      // Model Configs that run on them, in place of the Model Catalog and Model
      // Configs entries. The flag that gated both now gates this one.
      { name: 'Models', href: '/model-catalog', flagKey: 'innovate.fe.model-catalog.page' },
      { name: 'MCP Connections', href: '/mcp-connections', flagKey: 'innovate.fe.mcp-connections.page' },
    ],
  },
];

/**
 * Hook that evaluates every nav flag with a fixed call order (no conditional
 * hooks — the tiered nav model above is a module-level constant, so the set
 * and order of useFlag calls never changes at runtime). Returns a lookup of
 * flag name → enabled.
 */
function useNavFlags(): Record<FlagName, boolean> {
  return {
    'innovate.fe.dashboard.page': useFlag('innovate.fe.dashboard.page'),
    'innovate.fe.services.page': useFlag('innovate.fe.services.page'),
    'innovate.fe.endpoints.page': useFlag('innovate.fe.endpoints.page'),
    'innovate.fe.agents.page': useFlag('innovate.fe.agents.page'),
    'innovate.fe.prompts.page': useFlag('innovate.fe.prompts.page'),
    'innovate.fe.configs.page': useFlag('innovate.fe.configs.page'),
    'innovate.fe.message-routes.page': useFlag('innovate.fe.message-routes.page'),
    'innovate.fe.artifacts.page': useFlag('innovate.fe.artifacts.page'),
    'innovate.fe.knowledge.documents.page': useFlag('innovate.fe.knowledge.documents.page'),
    'innovate.fe.knowledge.bases.page': useFlag('innovate.fe.knowledge.bases.page'),
    'innovate.fe.model-catalog.page': useFlag('innovate.fe.model-catalog.page'),
    'innovate.fe.mcp-connections.page': useFlag('innovate.fe.mcp-connections.page'),
  } as Record<FlagName, boolean>;
}

/**
 * Active when the current path matches the link. Matches on path *segment*
 * boundaries (exact, or followed by `/`) so sibling prefixes don't collide,
 * while nested paths still highlight their section — e.g. `/services/:id`
 * and `/services/:id/runs/:collaborationId` light up the AI Services link.
 */
function isLinkActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

const NAV_ITEM_ACTIVE_CLASSES = 'border-indigo-500 text-gray-900 dark:text-white';
const NAV_ITEM_INACTIVE_CLASSES =
  'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300';

function NavGroupMenu({ group, items, pathname }: { group: NavGroup; items: NavLink[]; pathname: string }) {
  const isActive = items.some((item) => isLinkActive(pathname, item.href));

  return (
    <Menu as="div" className="relative flex">
      <MenuButton
        data-testid={group.testId}
        className={`inline-flex items-center gap-1 px-1 pt-1 border-b-2 text-sm font-medium ${
          isActive ? NAV_ITEM_ACTIVE_CLASSES : NAV_ITEM_INACTIVE_CLASSES
        }`}
      >
        <span>{group.name}</span>
        <ChevronDownIcon className="h-3.5 w-3.5" />
      </MenuButton>
      <MenuItems
        data-testid={`${group.testId}-panel`}
        className="absolute left-0 top-full z-50 mt-1 w-48 origin-top-left rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 py-1 focus:outline-none"
      >
        {items.map((item) => {
          const itemActive = isLinkActive(pathname, item.href);
          return (
            <MenuItem key={item.href}>
              <Link
                to={item.href}
                className={`block px-4 py-2 text-sm transition-colors data-[focus]:bg-gray-50 dark:data-[focus]:bg-gray-700 ${
                  itemActive
                    ? 'font-medium text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {item.name}
              </Link>
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
}

/**
 * The layout shell (task 720 split it out of `AppLayout`).
 *
 * `AppLayout` proper now decides whether the Assistant sidebar exists and — if
 * it does — mounts `AssistantProvider` around this shell, so the top nav's Help
 * menu, the tablet launcher and the sidebar itself all read one conversation.
 * Everything below the provider boundary lives here.
 */
function AppLayoutShell({
  children,
  fullWidth,
  sidebarEnabled,
  presentation,
}: AppLayoutProps & { sidebarEnabled: boolean; presentation: AssistantPresentation }) {
  const location = useLocation();
  const { layoutMode } = useLayoutMode();
  const { user } = useAuthContext();

  // The Assistant, when the sidebar has replaced the modal panel. Optional
  // access: with the flag off there is no provider, and this shell is the same
  // shell either way.
  const assistant = useOptionalAssistant();

  // Modal help assistant panel state (task 545) — the flag-off path. It lives
  // here rather than inside HelpMenu because the Menu unmounts its items on
  // close, and the panel — plus the conversation in it — has to outlive that.
  const [assistantOpen, setAssistantOpen] = useState(false);

  // "What's an AI Service?" explainer dialog state (task 605). Same reasoning,
  // and the same reason it is not the Dashboard's business: the entry has to
  // work from any page and at any account state, not only while the first-run
  // checklist is on screen.
  const [explainerOpen, setExplainerOpen] = useState(false);

  // Force a token refresh if we just returned from Stripe Checkout/Portal
  // so the new plan caps take effect without re-login.
  useBillingReturn();

  // Config edit/create pages benefit from full width for the canvas
  const isConfigCanvasPage =
    location.pathname === '/configs/new' ||
    (location.pathname.startsWith('/configs/') && location.pathname !== '/configs');

  // Use wide layout if prop says fullWidth OR user preference is wide OR on config canvas pages
  const shouldUseFullWidth = fullWidth || layoutMode === 'wide' || isConfigCanvasPage;

  /**
   * The New AI Service page (task 1117): the conversation is the page body, so
   * the docked pane shows only its rail, and at drawer/sheet widths — which
   * have no rail — neither the overlay nor its launcher appears.
   */
  const onAssistantPage = assistant?.presentation === 'page';

  // Evaluate all nav flags at once (fixed-order hook calls)
  const navFlags = useNavFlags();

  const visiblePlainLinks = PLAIN_LINKS.filter((item) => navFlags[item.flagKey]);

  // A group renders only when at least one of its children's flags is on
  const visibleGroups = NAV_GROUPS.map((group) => ({
    group,
    items: group.children.filter((item) => navFlags[item.flagKey]),
  })).filter(({ items }) => items.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* The navbar is app chrome, not page content, and it is pinned to the
          VIEWPORT (task 770) so navigation and the Help/avatar menus stay
          reachable however far down a long page the User has scrolled.

          Two consequences worth keeping together in one place:

          1. It always spans the full window (task 743), so it is deliberately
             NOT tied to `shouldUseFullWidth` — that boolean governs the <main>
             column only.
          2. The Assistant pane's `PINNED_PANE` (`sticky top-16`, task 766) is
             positioned on the assumption that this nav occupies the `h-16`
             band at the top of the window. Un-pinning the nav would leave the
             pane floating 4rem down with an empty band above it, so these two
             stay in step: the nav and the pane are one fixed frame.

          `z-30` sits above page content while staying below the `z-40` mobile
          launcher and the `z-50` dialogs; the nav's own Advanced/Help/User
          dropdowns are `z-50` inside it, so they open above the page. The
          background is deliberately opaque (`bg-white dark:bg-gray-800`) —
          content scrolling underneath must not show through. */}
      <nav className="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img
                  className="h-8 w-8 sm:mr-3"
                  src="/innovate-butterfly.png"
                  alt="Innovate Logo"
                />
                <h1 className="hidden sm:block text-xl font-bold text-gray-900 dark:text-white">Innovate</h1>
              </div>
              <div className="ml-4 lg:ml-6 flex space-x-3 sm:space-x-4 lg:space-x-8">
                {visiblePlainLinks.map((item) => {
                  const isActive = isLinkActive(location.pathname, item.href);

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                        isActive ? NAV_ITEM_ACTIVE_CLASSES : NAV_ITEM_INACTIVE_CLASSES
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
                {visibleGroups.map(({ group, items }) => (
                  <NavGroupMenu key={group.name} group={group} items={items} pathname={location.pathname} />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Help lives in the top nav, never in the avatar menu (task
                  538): documentation, the "What's an AI Service?" explainer,
                  the live examples, and a support contact. HelpMenu owns its
                  own flag check, and its help-assistant entry (task 545) owns a
                  second one — this layout just holds the overlays' open state
                  (the assistant panel, and the explainer dialog from task
                  605). */}
              {/* Tablet launcher (task 720): between the docked rail (desktop
                  has its own) and the floating button (mobile), the drawer's
                  entry point belongs in the nav. */}
              {sidebarEnabled && presentation === 'drawer' && !onAssistantPage && (
                <AssistantNavLauncher />
              )}
              {/* The Help menu's assistant entry OPENS THE SIDEBAR once the
                  sidebar exists; with the flag off it opens the modal panel it
                  always did, unchanged. */}
              <HelpMenu
                onOpenAssistant={() =>
                  sidebarEnabled && assistant ? assistant.open() : setAssistantOpen(true)
                }
                onOpenWhatIsAnAiService={() => setExplainerOpen(true)}
              />
              {user && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  {/* UsageIndicator is exception-based: it renders nothing below
                      its near-cap threshold, so the bar only carries usage when
                      it matters. Day-to-day usage lives on the billing page.
                      It wraps itself in a /settings/billing link when the
                      billing flag is on. */}
                  <UsageIndicator />
                  <UserMenu />
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Ambient no-plan signal (task 466): renders only while `user.plan` is
          empty and not session-dismissed; NoPlanBanner owns that logic. */}
      <NoPlanBanner />

      {/* Spending limit banners for owners and admins (task 1159): amber and
          dismissible once a threshold alert is sent, red and persistent while
          the hard stop refuses external calls. SpendingBanner owns the logic. */}
      {user && <SpendingBanner />}

      {/* Two-column shell (task 720): the Assistant docks BELOW the nav, so the
          brand, tiered navigation, Help menu, avatar menu and NoPlanBanner are
          untouched by its presence. `max-w-7xl` becomes the INNER width of the
          main column, which is what lets page content reflow beside the
          conversation instead of being overlaid by it. */}
      <div className="flex items-stretch">
        {sidebarEnabled &&
          (onAssistantPage ? (
            presentation === 'docked' && <AssistantSidebar presentation="page" />
          ) : (
            <AssistantSidebar presentation={presentation} />
          ))}

        <main
          className={`min-w-0 flex-1 ${
            shouldUseFullWidth
              ? 'py-6 px-4 sm:px-6 lg:px-8'
              : 'max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8'
          }`}
        >
          {children}
        </main>
      </div>

      {/* Mobile launcher (task 720) — the full-screen sheet's entry point. */}
      {sidebarEnabled && presentation === 'sheet' && !onAssistantPage && (
        <AssistantFloatingLauncher />
      )}

      {/* Docs-grounded help assistant (task 545) — the flag-off path only. Once
          the sidebar ships this modal container retires; its conversation
          internals moved into the sidebar. Mounting it alongside the sidebar
          would give the User two assistants, so it is genuinely either/or. */}
      {!sidebarEnabled && (
        <HelpAssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      )}

      {/* "What's an AI Service?" explainer (task 605), opened from the Help
          menu over whatever page the User is on. */}
      <WhatIsAnAiServiceDialog open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </div>
  );
}

/**
 * The application shell.
 *
 * Two gates decide whether the Assistant sidebar exists:
 *   1. `innovate.fe.help.assistant` — whether the assistant exists at all
 *      (unchanged, task 545).
 *   2. `innovate.fe.assistant.sidebar` — whether it is the docked sidebar or
 *      the original modal panel. Baked enabled per platform convention: this
 *      is a rollback lever, not a rollout gate, and turning it off restores the
 *      modal path exactly as it was.
 */
export default function AppLayout({ children, fullWidth = false }: AppLayoutProps) {
  const helpAssistantEnabled = useFlag('innovate.fe.help.assistant');
  const sidebarFlagEnabled = useFlag('innovate.fe.assistant.sidebar');
  const sidebarEnabled = helpAssistantEnabled && sidebarFlagEnabled;

  const presentation = useAssistantPresentation();
  const { sidebarState } = useAssistantSidebarPreference();

  const shell = (
    <AppLayoutShell
      fullWidth={fullWidth}
      sidebarEnabled={sidebarEnabled}
      presentation={presentation}
    >
      {children}
    </AppLayoutShell>
  );

  if (!sidebarEnabled) return shell;

  return (
    // Docked mode restores the stored open/collapsed choice; the overlay
    // presentations always start closed — a drawer or full-screen sheet that
    // opens itself on every navigation would be an ambush, not an assistant.
    <AssistantProvider
      initiallyOpen={presentation === 'docked' && sidebarState === 'open'}
      presentation={presentation}
    >
      {shell}
    </AssistantProvider>
  );
}
